import { RefObject, useCallback } from 'react';

import dayjs from 'dayjs';
import * as XLSX from 'xlsx';

interface UseTableExportValues {
  exportTableToXLSX: (
    tableRef: RefObject<HTMLTableElement>,
    fileName?: string
  ) => void;
  exportMultipleTablesToXLSX: (
    tableRefs: (HTMLTableElement | null)[],
    fileName?: string
  ) => void;
}

const generateFileName = (fileName: string): string => {
  const dateTimeString = dayjs().format('YYYY_MM_DD_HH_mm_ss');
  return `${fileName}_${dateTimeString}.xlsx`;
};

const removeClassElements = (element: HTMLElement, className: string) => {
  const elements = element.getElementsByClassName(className);
  while (elements.length > 0) {
    const parent = elements[0].parentNode;
    if (parent) parent.removeChild(elements[0]);
  }
};

const CSS_HIDDEN_EXPORT_CLASS = 'hidden-export';
function useTableExport(): UseTableExportValues {
  const exportTableToXLSX = useCallback(
    (tableRef: RefObject<HTMLTableElement>, fileName: string = 'report') => {
      if (tableRef.current) {
        const tableClone = tableRef.current.cloneNode(true) as HTMLTableElement;
        removeClassElements(tableClone, CSS_HIDDEN_EXPORT_CLASS);
        const wb = XLSX.utils.table_to_book(tableClone);
        XLSX.writeFile(wb, generateFileName(fileName));
      }
    },
    []
  );

  const exportMultipleTablesToXLSX = useCallback(
    (tableRefs: (HTMLTableElement | null)[], fileName: string = 'report') => {
      const wb = XLSX.utils.book_new();
      let sheetCount = 0;

      tableRefs.forEach((tableRef, index) => {
        if (!tableRef) return;
        const tableClone = tableRef.cloneNode(true) as HTMLTableElement;
        removeClassElements(tableClone, CSS_HIDDEN_EXPORT_CLASS);

        const ws = XLSX.utils.table_to_sheet(tableClone);
        const sheetName = `Sheet${index + 1}`;
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
        sheetCount += 1;
      });

      if (sheetCount > 0) {
        XLSX.writeFile(wb, generateFileName(fileName));
      } else {
        console.error('No valid tables found to export');
      }
    },
    []
  );

  return { exportTableToXLSX, exportMultipleTablesToXLSX };
}

export default useTableExport;
